import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  getBankTransactions, getBankTransactionsSummary, getFilterOptions,
  uploadPreview, importTransactions, updateBankTransaction,
  deleteBankTransaction, bulkCategorize, getTransactionCategories,
  createBankTransaction, getDeletedBankTransactions, restoreBankTransaction,
  permanentDeleteBankTransaction, bulkRestoreTransactions,
  bulkPermanentDeleteTransactions, purgeOldDeleted,
} from '@/lib/bankTransactionsApi';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import TransactionEditDialog from '@/components/TransactionEditDialog';
import AIReviewDialog from '@/components/AIReviewDialog';
import RulesDialog from '@/components/RulesDialog';
import CategoriesManagerDialog from '@/components/CategoriesManagerDialog';
import {
  Upload, Search, ArrowUpCircle, ArrowDownCircle, Sparkles, Tag,
  Plus, Trash2, X, AlertTriangle, FolderOpen, PenLine, Pencil, History,
  RotateCcw, Archive, Bookmark, Zap, Clock,
} from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (amt) => {
  if (amt == null) return '$0';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Math.abs(amt));
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  return `${m}/${d}/${y}`;
};

const monthLabel = (m) => {
  if (!m) return '';
  const [y, mo] = m.split('-');
  const names = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${names[parseInt(mo)]} ${y}`;
};

/* Helper: build display label from category/subcategory/detail */
const catLabel = (txn) => {
  const parts = [txn.category, txn.subcategory, txn.detail].filter(Boolean);
  return parts.length > 0 ? parts.join(' > ') : null;
};

/* Helper: get subcategory options from category object */
const getSubcats = (categories, catName) => {
  const cat = categories.find(c => c.name === catName);
  return cat?.subcategories || [];
};

/* Helper: get children from subcategory */
const getChildren = (categories, catName, subcatName) => {
  const subcats = getSubcats(categories, catName);
  const sub = subcats.find(s => s.name === subcatName);
  return sub?.children || [];
};

function AssignDescription({ description }) {
  const [expanded, setExpanded] = useState(false);
  if (!description) return <DialogDescription>—</DialogDescription>;
  const isLong = description.length > 60;
  if (!isLong) return <DialogDescription>{description}</DialogDescription>;
  return (
    <DialogDescription className="break-words">
      {expanded ? description : description.slice(0, 60) + '...'}
      <button
        onClick={() => setExpanded(!expanded)}
        className="ml-1 text-[11px] font-medium text-primary hover:underline"
        data-testid="description-toggle"
      >
        {expanded ? 'less' : 'more'}
      </button>
    </DialogDescription>
  );
}

export default function TransactionsPage() {
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState({ total_income: 0, total_expenses: 0, net: 0, count: 0 });
  const [filterOpts, setFilterOpts] = useState({ sources: [], banks: [], accounts: [], months: [] });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [filterType, setFilterType] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [filterBank, setFilterBank] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterUncategorized, setFilterUncategorized] = useState(false);

  // Upload
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadPreviewData, setUploadPreviewData] = useState(null);
  const [uploadApprovals, setUploadApprovals] = useState({});
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  // Category management
  const [catOpen, setCatOpen] = useState(false);

  // Category assignment (3-level)
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [assignCat, setAssignCat] = useState('');
  const [assignSubcat, setAssignSubcat] = useState('');
  const [assignDetail, setAssignDetail] = useState('');

  // Selection for bulk actions
  const [selected, setSelected] = useState(new Set());

  // Bulk category
  const [bulkCatOpen, setBulkCatOpen] = useState(false);
  const [bulkCat, setBulkCat] = useState('');
  const [bulkSubcat, setBulkSubcat] = useState('');
  const [bulkDetail, setBulkDetail] = useState('');

  // Manual entry
  const [manualOpen, setManualOpen] = useState(false);
  const [manualSaving, setManualSaving] = useState(false);
  const emptyManual = { date: new Date().toISOString().split('T')[0], title: '', description: '', amount: '', source: '', bank: '', account: '', card_member: '', category: '', subcategory: '', detail: '' };
  const [manualForm, setManualForm] = useState({ ...emptyManual });

  // Edit transaction (full edit)
  const [editOpen, setEditOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(-1);
  const editTarget = editIndex >= 0 && editIndex < transactions.length ? transactions[editIndex] : null;

  // AI Review dialog
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewTxns, setReviewTxns] = useState([]);

  // Delete confirmation
  const [deleteConfirmTxn, setDeleteConfirmTxn] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Deleted transactions viewer
  const [deletedOpen, setDeletedOpen] = useState(false);
  const [deletedTxns, setDeletedTxns] = useState([]);
  const [deletedLoading, setDeletedLoading] = useState(false);
  const [permanentDeleteConfirm, setPermanentDeleteConfirm] = useState(null);
  const [selectedDeleted, setSelectedDeleted] = useState(new Set());
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [purgeConfirm, setPurgeConfirm] = useState(false);
  const [purgeDays, setPurgeDays] = useState(30);
  const [purging, setPurging] = useState(false);

  // Rules dialog
  const [rulesOpen, setRulesOpen] = useState(false);
  const filterParams = useMemo(() => {
    const params = {};
    if (filterType) params.type = filterType;
    if (filterSource) params.source = filterSource;
    if (filterBank) params.bank = filterBank;
    if (filterCategory) params.category = filterCategory;
    if (filterMonth) params.month = filterMonth;
    if (filterFrom) params.date_from = filterFrom;
    if (filterTo) params.date_to = filterTo;
    if (searchTerm) params.search = searchTerm;
    if (filterUncategorized) params.uncategorized = true;
    return params;
  }, [filterType, filterSource, filterBank, filterCategory, filterMonth, filterFrom, filterTo, searchTerm, filterUncategorized]);

  const fetchData = useCallback(async () => {
    try {
      const [txns, sum, opts, cats] = await Promise.all([
        getBankTransactions(filterParams),
        getBankTransactionsSummary(filterParams),
        getFilterOptions(),
        getTransactionCategories(),
      ]);
      setTransactions(txns);
      setSummary(sum);
      setFilterOpts(opts);
      setCategories(cats);
    } catch {
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [filterParams]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const hasFilters = filterType || filterSource || filterBank || filterCategory || filterMonth || filterFrom || filterTo || searchTerm || filterUncategorized;

  const clearFilters = () => {
    setFilterType(''); setFilterSource(''); setFilterBank('');
    setFilterCategory(''); setFilterMonth(''); setFilterFrom('');
    setFilterTo(''); setSearchTerm(''); setFilterUncategorized(false);
  };

  // ─── Upload handlers ───
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      const txns = Array.isArray(json) ? json : [json];
      const preview = await uploadPreview(txns);
      setUploadPreviewData(preview);
      const approvals = {};
      preview.transactions.forEach((t, i) => { approvals[i] = !t.is_duplicate; });
      setUploadApprovals(approvals);
      setUploadOpen(true);
    } catch { toast.error('Failed to parse JSON file'); }
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleImport = async () => {
    if (!uploadPreviewData) return;
    setImporting(true);
    try {
      const toImport = uploadPreviewData.transactions.map((t, i) => ({ ...t, approved: uploadApprovals[i] ?? false }));
      const result = await importTransactions(toImport);
      toast.success(`Imported ${result.imported} transactions`);
      setUploadOpen(false);
      setUploadPreviewData(null);
      fetchData();
    } catch { toast.error('Import failed'); }
    finally { setImporting(false); }
  };

  // ─── Category assignment (3-level) ───
  const openAssign = (txn) => {
    setAssignTarget(txn);
    setAssignCat(txn.category || '');
    setAssignSubcat(txn.subcategory || '');
    setAssignDetail(txn.detail || '');
    setAssignOpen(true);
  };

  const handleAssign = async () => {
    if (!assignTarget) return;
    try {
      await updateBankTransaction(assignTarget.id, {
        category: assignCat || '',
        subcategory: assignSubcat || '',
        detail: assignDetail || '',
      });
      toast.success('Category updated');
      setAssignOpen(false);
      fetchData();
    } catch { toast.error('Failed to update category'); }
  };

  // ─── Selection ───
  const toggleSelect = (id) => {
    setSelected(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  };
  const toggleSelectAll = () => {
    if (selected.size === transactions.length) setSelected(new Set());
    else setSelected(new Set(transactions.map(t => t.id)));
  };

  // ─── AI Categorize (opens review modal) ───
  const handleAICategorize = () => {
    const ids = Array.from(selected);
    if (ids.length === 0) { toast.error('Select transactions to categorize'); return; }
    const selectedTxns = transactions.filter(t => ids.includes(t.id));
    setReviewTxns(selectedTxns);
    setReviewOpen(true);
  };

  const handleReviewComplete = (appliedCount) => {
    toast.success(`Applied ${appliedCount} categorizations`);
    setSelected(new Set());
    fetchData();
  };

  // ─── Soft delete with confirmation ───
  const handleConfirmDelete = async () => {
    if (!deleteConfirmTxn) return;
    setDeleting(true);
    try {
      await deleteBankTransaction(deleteConfirmTxn.id);
      toast.success('Transaction moved to deleted');
      setDeleteConfirmTxn(null);
      fetchData();
    } catch { toast.error('Failed to delete'); }
    finally { setDeleting(false); }
  };

  // ─── Open deleted viewer ───
  const openDeletedViewer = async () => {
    setDeletedOpen(true);
    setDeletedLoading(true);
    setSelectedDeleted(new Set());
    try {
      const data = await getDeletedBankTransactions();
      setDeletedTxns(data);
    } catch { toast.error('Failed to load deleted'); }
    finally { setDeletedLoading(false); }
  };

  const toggleDeletedSelect = (id) => {
    setSelectedDeleted(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };
  const toggleDeletedSelectAll = () => {
    if (selectedDeleted.size === deletedTxns.length) setSelectedDeleted(new Set());
    else setSelectedDeleted(new Set(deletedTxns.map(t => t.id)));
  };

  const handleBulkRestore = async () => {
    const ids = Array.from(selectedDeleted);
    if (ids.length === 0) return;
    try {
      const res = await bulkRestoreTransactions(ids);
      toast.success(`Restored ${res.restored}`);
      setDeletedTxns(prev => prev.filter(t => !selectedDeleted.has(t.id)));
      setSelectedDeleted(new Set());
      fetchData();
    } catch { toast.error('Bulk restore failed'); }
  };

  const handleBulkPermanentDelete = async () => {
    const ids = Array.from(selectedDeleted);
    if (ids.length === 0) return;
    try {
      const res = await bulkPermanentDeleteTransactions(ids);
      toast.success(`Permanently deleted ${res.deleted}`);
      setDeletedTxns(prev => prev.filter(t => !selectedDeleted.has(t.id)));
      setSelectedDeleted(new Set());
      setBulkDeleteConfirm(false);
    } catch { toast.error('Bulk delete failed'); }
  };

  const handlePurgeOld = async () => {
    setPurging(true);
    try {
      const res = await purgeOldDeleted(purgeDays);
      toast.success(`Purged ${res.purged} old deleted transaction${res.purged === 1 ? '' : 's'}`);
      setPurgeConfirm(false);
      // Refresh
      const data = await getDeletedBankTransactions();
      setDeletedTxns(data);
      setSelectedDeleted(new Set());
    } catch { toast.error('Purge failed'); }
    finally { setPurging(false); }
  };

  const handleRestore = async (txn) => {
    try {
      await restoreBankTransaction(txn.id);
      toast.success('Restored');
      setDeletedTxns(prev => prev.filter(t => t.id !== txn.id));
      fetchData();
    } catch { toast.error('Failed to restore'); }
  };

  const handlePermanentDelete = async () => {
    if (!permanentDeleteConfirm) return;
    try {
      await permanentDeleteBankTransaction(permanentDeleteConfirm.id);
      toast.success('Permanently deleted');
      setDeletedTxns(prev => prev.filter(t => t.id !== permanentDeleteConfirm.id));
      setPermanentDeleteConfirm(null);
    } catch { toast.error('Failed to permanently delete'); }
  };

  // ─── Bulk category ───
  const handleBulkCategorize = async () => {
    const ids = Array.from(selected);
    if (!ids.length || !bulkCat) return;
    try {
      await bulkCategorize({ transaction_ids: ids, category: bulkCat, subcategory: bulkSubcat || null, detail: bulkDetail || null });
      toast.success(`Updated ${ids.length} transactions`);
      setBulkCatOpen(false); setBulkCat(''); setBulkSubcat(''); setBulkDetail('');
      setSelected(new Set());
      fetchData();
    } catch { toast.error('Bulk update failed'); }
  };

  // ─── Manual entry ───
  const handleManualSave = async () => {
    if (!manualForm.date || !manualForm.description || !manualForm.amount) {
      toast.error('Date, description, and amount are required');
      return;
    }
    setManualSaving(true);
    try {
      await createBankTransaction({
        date: manualForm.date,
        title: manualForm.title || '',
        description: manualForm.description,
        amount: parseFloat(manualForm.amount),
        source: manualForm.source || '',
        bank: manualForm.bank || '',
        account: manualForm.account || '',
        card_member: manualForm.card_member || null,
        category: manualForm.category || null,
        subcategory: manualForm.subcategory || null,
        detail: manualForm.detail || null,
      });
      toast.success('Transaction added');
      setManualOpen(false);
      setManualForm({ ...emptyManual });
      fetchData();
    } catch { toast.error('Failed to add transaction'); }
    finally { setManualSaving(false); }
  };

  const manualSubcatOptions = useMemo(() => getSubcats(categories, manualForm.category), [manualForm.category, categories]);
  const manualDetailOptions = useMemo(() => getChildren(categories, manualForm.category, manualForm.subcategory), [manualForm.category, manualForm.subcategory, categories]);

  // ─── Derived data for selects ───
  const assignSubcatOptions = useMemo(() => getSubcats(categories, assignCat), [assignCat, categories]);
  const assignDetailOptions = useMemo(() => getChildren(categories, assignCat, assignSubcat), [assignCat, assignSubcat, categories]);
  const bulkSubcatOptions = useMemo(() => getSubcats(categories, bulkCat), [bulkCat, categories]);
  const bulkDetailOptions = useMemo(() => getChildren(categories, bulkCat, bulkSubcat), [bulkCat, bulkSubcat, categories]);

  if (loading) return <div className="py-12 text-center text-muted-foreground text-sm">Loading...</div>;

  const approvedCount = uploadPreviewData ? Object.values(uploadApprovals).filter(Boolean).length : 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight" data-testid="transactions-page-title">Transactions</h1>
          <p className="text-sm text-muted-foreground mt-1">Upload and categorize bank & card transactions</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setCatOpen(true)} data-testid="manage-categories-btn">
            <Tag className="h-3.5 w-3.5 mr-1.5" />Categories
          </Button>
          <Button variant="outline" size="sm" onClick={() => setRulesOpen(true)} data-testid="manage-rules-btn">
            <Bookmark className="h-3.5 w-3.5 mr-1.5" />Rules
          </Button>
          <Button variant="outline" size="sm" onClick={openDeletedViewer} data-testid="deleted-transactions-btn">
            <Archive className="h-3.5 w-3.5 mr-1.5" />Deleted
          </Button>
          <Button variant="outline" onClick={() => { setManualForm({ ...emptyManual }); setManualOpen(true); }} data-testid="add-transaction-btn">
            <Plus className="h-4 w-4 mr-1.5" />Add Transaction
          </Button>
          <input ref={fileRef} type="file" accept=".json" className="hidden" onChange={handleFileSelect} data-testid="json-file-input" />
          <Button onClick={() => fileRef.current?.click()} data-testid="upload-json-btn">
            <Upload className="h-4 w-4 mr-1.5" />Upload JSON
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="summary-cards">
        <div className="bg-white rounded-lg border border-border/60 p-3.5">
          <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Transactions</p>
          <p className="text-xl font-heading font-semibold tabular-nums mt-1" data-testid="summary-count">{summary.count}</p>
        </div>
        <div className="bg-white rounded-lg border border-border/60 p-3.5">
          <p className="text-[11px] font-medium text-emerald-600 uppercase tracking-wide">Total Income</p>
          <p className="text-xl font-heading font-semibold tabular-nums text-emerald-700 mt-1" data-testid="summary-income">{formatCurrency(summary.total_income)}</p>
        </div>
        <div className="bg-white rounded-lg border border-border/60 p-3.5">
          <p className="text-[11px] font-medium text-red-500 uppercase tracking-wide">Total Expenses</p>
          <p className="text-xl font-heading font-semibold tabular-nums text-red-700 mt-1" data-testid="summary-expenses">{formatCurrency(summary.total_expenses)}</p>
        </div>
        <div className="bg-white rounded-lg border border-border/60 p-3.5">
          <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Net</p>
          <p className={`text-xl font-heading font-semibold tabular-nums mt-1 ${summary.net >= 0 ? 'text-emerald-700' : 'text-red-700'}`} data-testid="summary-net">
            {summary.net >= 0 ? '+' : '-'}{formatCurrency(summary.net)}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-end gap-2 flex-wrap" data-testid="filter-bar">
        <div className="relative flex-1 min-w-[160px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="h-8 text-xs pl-8" placeholder="Search title, description, or amount..." data-testid="txn-search" />
        </div>
        <Select value={filterType || '__all__'} onValueChange={v => setFilterType(v === '__all__' ? '' : v)}>
          <SelectTrigger className="h-8 w-28 text-xs" data-testid="filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent><SelectItem value="__all__">All Types</SelectItem><SelectItem value="income">Income</SelectItem><SelectItem value="expense">Expense</SelectItem></SelectContent>
        </Select>
        {filterOpts.banks.length > 0 && (
          <Select value={filterBank || '__all__'} onValueChange={v => setFilterBank(v === '__all__' ? '' : v)}>
            <SelectTrigger className="h-8 w-28 text-xs" data-testid="filter-bank"><SelectValue placeholder="Bank" /></SelectTrigger>
            <SelectContent><SelectItem value="__all__">All Banks</SelectItem>{filterOpts.banks.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {filterOpts.sources.length > 0 && (
          <Select value={filterSource || '__all__'} onValueChange={v => setFilterSource(v === '__all__' ? '' : v)}>
            <SelectTrigger className="h-8 w-32 text-xs" data-testid="filter-source"><SelectValue placeholder="Source" /></SelectTrigger>
            <SelectContent><SelectItem value="__all__">All Sources</SelectItem>{filterOpts.sources.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {categories.length > 0 && (
          <Select value={filterCategory || '__all__'} onValueChange={v => setFilterCategory(v === '__all__' ? '' : v)}>
            <SelectTrigger className="h-8 w-36 text-xs" data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent><SelectItem value="__all__">All Categories</SelectItem>{categories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {filterOpts.months.length > 0 && (
          <Select value={filterMonth || '__all__'} onValueChange={v => setFilterMonth(v === '__all__' ? '' : v)}>
            <SelectTrigger className="h-8 w-32 text-xs" data-testid="filter-month"><SelectValue placeholder="Month" /></SelectTrigger>
            <SelectContent><SelectItem value="__all__">All Months</SelectItem>{filterOpts.months.map(m => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <Input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)} className="h-8 w-36 text-xs" data-testid="filter-from" />
        <span className="text-xs text-muted-foreground">to</span>
        <Input type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)} className="h-8 w-36 text-xs" data-testid="filter-to" />
        <Button variant={filterUncategorized ? 'secondary' : 'outline'} size="sm" className="h-8 text-xs" onClick={() => setFilterUncategorized(!filterUncategorized)} data-testid="filter-uncategorized">
          <FolderOpen className="h-3 w-3 mr-1" />Uncategorized
        </Button>
        {filterUncategorized && transactions.length > 0 && (
          <Button
            size="sm"
            className="h-8 text-xs"
            onClick={() => {
              const ids = transactions.map(t => t.id);
              setSelected(new Set(ids));
              setReviewTxns(transactions);
              setReviewOpen(true);
            }}
            data-testid="review-all-uncategorized-btn"
          >
            <Sparkles className="h-3 w-3 mr-1" />Review All ({transactions.length})
          </Button>
        )}
        {hasFilters && <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={clearFilters} data-testid="clear-filters-btn">Clear</Button>}
      </div>

      {/* Bulk Actions */}
      {selected.size > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 bg-[#f5f0eb] rounded-lg border border-border/50" data-testid="bulk-actions-bar">
          <span className="text-xs font-medium">{selected.size} selected</span>
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setBulkCatOpen(true)} data-testid="bulk-assign-btn">
            <Tag className="h-3 w-3 mr-1" />Assign Category
          </Button>
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={handleAICategorize} data-testid="ai-categorize-btn">
            <Sparkles className="h-3 w-3 mr-1" />AI Categorize
          </Button>
          <Button variant="ghost" size="sm" className="h-7 text-xs ml-auto" onClick={() => setSelected(new Set())} data-testid="clear-selection-btn">
            <X className="h-3 w-3 mr-1" />Clear
          </Button>
        </div>
      )}

      {/* Table */}
      {transactions.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground border rounded-lg bg-muted/20" data-testid="txn-empty-state">
          <Upload className="h-8 w-8 mx-auto mb-3 opacity-40" />
          <p className="text-sm font-medium">No transactions yet</p>
          <p className="text-xs mt-1">Upload a JSON file to get started</p>
        </div>
      ) : (
        <div className="rounded-lg border border-border/60 overflow-hidden bg-white" data-testid="transactions-table-container">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="transactions-table">
              <thead>
                <tr className="bg-[#f5f0eb] text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground border-b border-border/50">
                  <th className="px-3 py-2.5 w-8">
                    <input type="checkbox" checked={selected.size === transactions.length && transactions.length > 0} onChange={toggleSelectAll} className="rounded border-gray-300" data-testid="select-all-checkbox" />
                  </th>
                  <th className="px-3 py-2.5">Date</th>
                  <th className="px-3 py-2.5">Title</th>
                  <th className="px-3 py-2.5 text-right">Amount</th>
                  <th className="px-3 py-2.5">Source</th>
                  <th className="px-3 py-2.5">Origin</th>
                  <th className="px-3 py-2.5">Category</th>
                  <th className="px-3 py-2.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((txn, idx) => {
                  const label = catLabel(txn);
                  const hasHistory = (txn.edit_history || []).length > 0;
                  const dateEdits = (txn.edit_history || []).filter(h => h.field === 'date').length;
                  const amountEdits = (txn.edit_history || []).filter(h => h.field === 'amount').length;
                  return (
                    <tr key={txn.id} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-[#fafaf7]'} hover:bg-[#f0ece6] transition-colors border-b border-border/30 last:border-b-0`} data-testid="transaction-row">
                      <td className="px-3 py-2">
                        <input type="checkbox" checked={selected.has(txn.id)} onChange={() => toggleSelect(txn.id)} className="rounded border-gray-300" data-testid="txn-select-checkbox" />
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap tabular-nums text-xs">
                        <span className="inline-flex items-center gap-1">
                          {formatDate(txn.date)}
                          {dateEdits > 0 && (
                            <button
                              onClick={() => { setEditIndex(idx); setEditOpen(true); }}
                              title={`Date edited ${dateEdits}x — click to view history`}
                              className="text-amber-600 hover:text-amber-700"
                              data-testid="date-edited-marker"
                            >
                              <History className="h-3 w-3" />
                            </button>
                          )}
                        </span>
                      </td>
                      <td className="px-3 py-2 max-w-[300px]">
                        {txn.title ? (
                          <span className="text-xs font-medium truncate block" data-testid="txn-title">{txn.title}</span>
                        ) : (
                          <span className="text-xs italic text-muted-foreground truncate block" data-testid="txn-title-empty">{txn.description || '—'}</span>
                        )}
                        {txn.card_member && <span className="text-[10px] text-muted-foreground block mt-0.5">{txn.card_member}</span>}
                      </td>
                      <td className={`px-3 py-2 whitespace-nowrap font-semibold tabular-nums text-xs text-right ${txn.type === 'income' ? 'text-emerald-700' : 'text-red-700'}`}>
                        <span className="inline-flex items-center gap-1 justify-end">
                          {amountEdits > 0 && (
                            <button
                              onClick={() => { setEditIndex(idx); setEditOpen(true); }}
                              title={`Amount edited ${amountEdits}x — click to view history`}
                              className="text-amber-600 hover:text-amber-700"
                              data-testid="amount-edited-marker"
                            >
                              <History className="h-3 w-3" />
                            </button>
                          )}
                          {txn.type === 'income' ? <ArrowUpCircle className="h-3 w-3 text-emerald-500 flex-shrink-0" /> : <ArrowDownCircle className="h-3 w-3 text-red-400 flex-shrink-0" />}
                          {txn.type === 'income' ? '+' : '-'}{formatCurrency(txn.amount)}
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {txn.source ? <span className="text-[10px] px-1.5 py-0.5 rounded border bg-muted/30 text-muted-foreground">{txn.source}</span> : <span className="text-[10px] text-muted-foreground">—</span>}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {txn.origin === 'manual' ? (
                          <span className="text-[10px] px-1.5 py-0.5 rounded border border-teal-200 bg-teal-50 text-teal-700 inline-flex items-center gap-0.5" data-testid="origin-manual-badge"><PenLine className="h-2.5 w-2.5" />Manual</span>
                        ) : (
                          <span className="text-[10px] px-1.5 py-0.5 rounded border border-blue-200 bg-blue-50 text-blue-700 inline-flex items-center gap-0.5" data-testid="origin-json-badge"><Upload className="h-2.5 w-2.5" />JSON</span>
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {label ? (
                          <button onClick={() => openAssign(txn)} className="inline-flex items-center gap-1 cursor-pointer hover:opacity-80 max-w-[200px]" data-testid="txn-category-badge">
                            <Badge variant="secondary" className="text-[10px] truncate max-w-[200px]">{label}</Badge>
                          </button>
                        ) : (
                          <button onClick={() => openAssign(txn)} className="text-xs text-muted-foreground hover:text-foreground hover:underline" data-testid="txn-assign-category-btn">+ Assign</button>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-0.5">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-primary" onClick={() => { setEditIndex(idx); setEditOpen(true); }} title="Edit" data-testid="txn-edit-btn">
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-red-600" onClick={() => setDeleteConfirmTxn(txn)} title="Delete" data-testid="txn-delete-btn"><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Upload Preview Dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import Preview</DialogTitle>
            <DialogDescription>
              {uploadPreviewData && (
                <span>
                  {uploadPreviewData.total} transactions found.
                  {uploadPreviewData.duplicates > 0 && <span className="text-amber-600 font-medium"> {uploadPreviewData.duplicates} duplicates detected.</span>}
                  {uploadPreviewData.rule_hits > 0 && <span className="text-sky-700 font-medium"> {uploadPreviewData.rule_hits} will auto-categorize via rules.</span>}
                  {' '}{approvedCount} will be imported.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          {uploadPreviewData && (
            <div className="space-y-2 max-h-[50vh] overflow-y-auto">
              {uploadPreviewData.transactions.map((txn, i) => (
                <div key={i} className={`flex items-center gap-3 p-2.5 rounded-lg border text-xs ${txn.is_duplicate ? 'border-amber-200 bg-amber-50/50' : 'border-border/50 bg-white'}`} data-testid="upload-preview-row">
                  <input type="checkbox" checked={uploadApprovals[i] ?? false} onChange={() => setUploadApprovals(prev => ({ ...prev, [i]: !prev[i] }))} className="rounded border-gray-300 flex-shrink-0" data-testid="upload-approve-checkbox" />
                  <span className="w-20 tabular-nums text-muted-foreground flex-shrink-0">{formatDate(txn.date)}</span>
                  <span className="flex-1 truncate font-medium">{txn.description}</span>
                  <span className={`w-24 text-right tabular-nums font-semibold flex-shrink-0 ${txn.type === 'income' ? 'text-emerald-700' : 'text-red-700'}`}>{txn.type === 'income' ? '+' : '-'}{formatCurrency(txn.amount)}</span>
                  <span className="w-20 text-muted-foreground flex-shrink-0">{txn.bank}</span>
                  {txn.rule_preview && !txn.is_duplicate && (
                    <Badge
                      variant="outline"
                      className="text-sky-700 border-sky-300 bg-sky-50 text-[10px] flex-shrink-0 gap-1"
                      title={`Rule: ${txn.rule_preview.pattern}`}
                      data-testid="upload-rule-badge"
                    >
                      <Bookmark className="h-2.5 w-2.5" />
                      {txn.rule_preview.category}
                      {txn.rule_preview.subcategory ? ` › ${txn.rule_preview.subcategory}` : ''}
                    </Badge>
                  )}
                  {txn.is_duplicate && <Badge variant="outline" className="text-amber-700 border-amber-300 text-[10px] flex-shrink-0"><AlertTriangle className="h-2.5 w-2.5 mr-1" />Duplicate</Badge>}
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)}>Cancel</Button>
            <Button onClick={handleImport} disabled={importing || approvedCount === 0} data-testid="confirm-import-btn">{importing ? 'Importing...' : `Import ${approvedCount} Transactions`}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Category Dialog (3-level) */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Assign Category</DialogTitle>
            <AssignDescription description={assignTarget?.description} />
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>Category</Label>
              <Select value={assignCat || '__none__'} onValueChange={v => { setAssignCat(v === '__none__' ? '' : v); setAssignSubcat(''); setAssignDetail(''); }}>
                <SelectTrigger data-testid="assign-category-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>{[<SelectItem key="__none__" value="__none__">None</SelectItem>, ...categories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)]}</SelectContent>
              </Select>
            </div>
            {assignSubcatOptions.length > 0 && (
              <div className="space-y-1">
                <Label>Subcategory</Label>
                <Select value={assignSubcat || '__none__'} onValueChange={v => { setAssignSubcat(v === '__none__' ? '' : v); setAssignDetail(''); }}>
                  <SelectTrigger data-testid="assign-subcategory-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>{[<SelectItem key="__none__" value="__none__">None</SelectItem>, ...assignSubcatOptions.map(s => <SelectItem key={s.name} value={s.name}>{s.name}</SelectItem>)]}</SelectContent>
                </Select>
              </div>
            )}
            {assignDetailOptions.length > 0 && (
              <div className="space-y-1">
                <Label>Detail</Label>
                <Select value={assignDetail || '__none__'} onValueChange={v => setAssignDetail(v === '__none__' ? '' : v)}>
                  <SelectTrigger data-testid="assign-detail-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>{[<SelectItem key="__none__" value="__none__">None</SelectItem>, ...assignDetailOptions.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)]}</SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignOpen(false)}>Cancel</Button>
            <Button onClick={handleAssign} data-testid="assign-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Assign Dialog (3-level) */}
      <Dialog open={bulkCatOpen} onOpenChange={setBulkCatOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Bulk Assign Category</DialogTitle>
            <DialogDescription>Apply to {selected.size} selected transactions</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>Category</Label>
              <Select value={bulkCat || '__none__'} onValueChange={v => { setBulkCat(v === '__none__' ? '' : v); setBulkSubcat(''); setBulkDetail(''); }}>
                <SelectTrigger data-testid="bulk-category-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>{[<SelectItem key="__none__" value="__none__">None</SelectItem>, ...categories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)]}</SelectContent>
              </Select>
            </div>
            {bulkSubcatOptions.length > 0 && (
              <div className="space-y-1">
                <Label>Subcategory</Label>
                <Select value={bulkSubcat || '__none__'} onValueChange={v => { setBulkSubcat(v === '__none__' ? '' : v); setBulkDetail(''); }}>
                  <SelectTrigger data-testid="bulk-subcategory-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>{[<SelectItem key="__none__" value="__none__">None</SelectItem>, ...bulkSubcatOptions.map(s => <SelectItem key={s.name} value={s.name}>{s.name}</SelectItem>)]}</SelectContent>
                </Select>
              </div>
            )}
            {bulkDetailOptions.length > 0 && (
              <div className="space-y-1">
                <Label>Detail</Label>
                <Select value={bulkDetail || '__none__'} onValueChange={v => setBulkDetail(v === '__none__' ? '' : v)}>
                  <SelectTrigger data-testid="bulk-detail-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>{[<SelectItem key="__none__" value="__none__">None</SelectItem>, ...bulkDetailOptions.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)]}</SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkCatOpen(false)}>Cancel</Button>
            <Button onClick={handleBulkCategorize} disabled={!bulkCat} data-testid="bulk-save-btn">Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Management Dialog */}
      <CategoriesManagerDialog
        open={catOpen}
        onOpenChange={setCatOpen}
        onChanged={() => fetchData()}
      />

      {/* Manual Entry Dialog */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Transaction</DialogTitle>
            <DialogDescription>Manually enter a transaction</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Date *</Label>
                <Input type="date" value={manualForm.date} onChange={e => setManualForm(f => ({ ...f, date: e.target.value }))} data-testid="manual-date" />
              </div>
              <div className="space-y-1">
                <Label>Amount *</Label>
                <Input type="number" step="0.01" value={manualForm.amount} onChange={e => setManualForm(f => ({ ...f, amount: e.target.value }))} placeholder="Positive = income, negative = expense" data-testid="manual-amount" />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Title</Label>
              <Input value={manualForm.title} onChange={e => setManualForm(f => ({ ...f, title: e.target.value }))} placeholder="Short label (e.g., Rent from Unit 2)" data-testid="manual-title" />
            </div>
            <div className="space-y-1">
              <Label>Description *</Label>
              <Input value={manualForm.description} onChange={e => setManualForm(f => ({ ...f, description: e.target.value }))} placeholder="e.g., Zelle payment from John for rent" data-testid="manual-description" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label>Source</Label>
                <Input value={manualForm.source} onChange={e => setManualForm(f => ({ ...f, source: e.target.value }))} placeholder="e.g., bank_checking" className="text-xs" data-testid="manual-source" />
              </div>
              <div className="space-y-1">
                <Label>Bank</Label>
                <Input value={manualForm.bank} onChange={e => setManualForm(f => ({ ...f, bank: e.target.value }))} placeholder="e.g., BOA" className="text-xs" data-testid="manual-bank" />
              </div>
              <div className="space-y-1">
                <Label>Account</Label>
                <Input value={manualForm.account} onChange={e => setManualForm(f => ({ ...f, account: e.target.value }))} placeholder="e.g., boa_7988" className="text-xs" data-testid="manual-account" />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Card Member</Label>
              <Input value={manualForm.card_member} onChange={e => setManualForm(f => ({ ...f, card_member: e.target.value }))} placeholder="Optional" className="text-xs" data-testid="manual-card-member" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label>Category</Label>
                <Select value={manualForm.category || '__none__'} onValueChange={v => setManualForm(f => ({ ...f, category: v === '__none__' ? '' : v, subcategory: '', detail: '' }))}>
                  <SelectTrigger className="text-xs" data-testid="manual-category"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>{[<SelectItem key="__none__" value="__none__">None</SelectItem>, ...categories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)]}</SelectContent>
                </Select>
              </div>
              {manualSubcatOptions.length > 0 && (
                <div className="space-y-1">
                  <Label>Subcategory</Label>
                  <Select value={manualForm.subcategory || '__none__'} onValueChange={v => setManualForm(f => ({ ...f, subcategory: v === '__none__' ? '' : v, detail: '' }))}>
                    <SelectTrigger className="text-xs" data-testid="manual-subcategory"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>{[<SelectItem key="__none__" value="__none__">None</SelectItem>, ...manualSubcatOptions.map(s => <SelectItem key={s.name} value={s.name}>{s.name}</SelectItem>)]}</SelectContent>
                  </Select>
                </div>
              )}
              {manualDetailOptions.length > 0 && (
                <div className="space-y-1">
                  <Label>Detail</Label>
                  <Select value={manualForm.detail || '__none__'} onValueChange={v => setManualForm(f => ({ ...f, detail: v === '__none__' ? '' : v }))}>
                    <SelectTrigger className="text-xs" data-testid="manual-detail"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>{[<SelectItem key="__none__" value="__none__">None</SelectItem>, ...manualDetailOptions.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)]}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualOpen(false)}>Cancel</Button>
            <Button onClick={handleManualSave} disabled={manualSaving} data-testid="manual-save-btn">
              {manualSaving ? 'Adding...' : 'Add Transaction'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Full Edit Transaction Dialog */}
      <TransactionEditDialog
        open={editOpen}
        onOpenChange={(v) => { setEditOpen(v); if (!v) setEditIndex(-1); }}
        transaction={editTarget}
        categories={categories}
        onSaved={() => fetchData()}
        onPrev={() => setEditIndex(i => Math.max(0, i - 1))}
        onNext={() => setEditIndex(i => Math.min(transactions.length - 1, i + 1))}
        hasPrev={editIndex > 0}
        hasNext={editIndex >= 0 && editIndex < transactions.length - 1}
        position={editIndex >= 0 ? { current: editIndex + 1, total: transactions.length } : null}
      />

      {/* AI Review Dialog */}
      <AIReviewDialog
        open={reviewOpen}
        onOpenChange={setReviewOpen}
        transactions={reviewTxns}
        categories={categories}
        onComplete={handleReviewComplete}
      />

      {/* Rules Dialog */}
      <RulesDialog
        open={rulesOpen}
        onOpenChange={setRulesOpen}
        onApplied={() => fetchData()}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirmTxn} onOpenChange={(v) => { if (!v) setDeleteConfirmTxn(null); }}>
        <DialogContent className="max-w-sm" data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              Delete transaction?
            </DialogTitle>
            <DialogDescription>
              This transaction will be moved to <span className="font-medium">Deleted Transactions</span> and can be restored later.
            </DialogDescription>
          </DialogHeader>
          {deleteConfirmTxn && (
            <div className="rounded-lg border border-border/60 bg-[#faf8f4] p-3 text-xs space-y-1">
              <p className="font-medium text-[#1f2933]">{deleteConfirmTxn.title || deleteConfirmTxn.description}</p>
              <div className="flex items-center justify-between text-muted-foreground">
                <span>{formatDate(deleteConfirmTxn.date)}</span>
                <span className={`font-semibold tabular-nums ${deleteConfirmTxn.type === 'income' ? 'text-emerald-700' : 'text-red-700'}`}>
                  {deleteConfirmTxn.type === 'income' ? '+' : '-'}{formatCurrency(deleteConfirmTxn.amount)}
                </span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmTxn(null)} data-testid="cancel-delete-btn">Cancel</Button>
            <Button variant="destructive" onClick={handleConfirmDelete} disabled={deleting} data-testid="confirm-delete-btn">
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deleted Transactions Viewer */}
      <Dialog open={deletedOpen} onOpenChange={setDeletedOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="deleted-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive className="h-4 w-4" />
              Deleted Transactions
            </DialogTitle>
            <DialogDescription>
              {deletedLoading ? 'Loading...' : `${deletedTxns.length} deleted transaction${deletedTxns.length === 1 ? '' : 's'} — select rows for bulk actions, or purge items older than N days`}
            </DialogDescription>
          </DialogHeader>
          {/* Bulk action bar */}
          {deletedTxns.length > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 bg-[#f5f0eb] rounded-md border border-border/40 text-xs" data-testid="deleted-bulk-bar">
              {selectedDeleted.size > 0 ? (
                <>
                  <span className="font-medium">{selectedDeleted.size} selected</span>
                  <Button variant="outline" size="sm" className="h-7 text-[11px] text-primary gap-1" onClick={handleBulkRestore} data-testid="bulk-restore-btn">
                    <RotateCcw className="h-3 w-3" />Restore
                  </Button>
                  <Button variant="outline" size="sm" className="h-7 text-[11px] text-red-600 hover:bg-red-50 gap-1" onClick={() => setBulkDeleteConfirm(true)} data-testid="bulk-permanent-delete-btn">
                    <Trash2 className="h-3 w-3" />Delete Forever
                  </Button>
                  <Button variant="ghost" size="sm" className="h-7 text-[11px] ml-auto" onClick={() => setSelectedDeleted(new Set())} data-testid="clear-deleted-selection-btn">
                    <X className="h-3 w-3 mr-1" />Clear
                  </Button>
                </>
              ) : (
                <>
                  <Clock className="h-3 w-3 text-muted-foreground" />
                  <span className="text-muted-foreground">Auto-purge:</span>
                  <Input
                    type="number" min="1" value={purgeDays}
                    onChange={e => setPurgeDays(parseInt(e.target.value) || 30)}
                    className="h-7 w-16 text-xs"
                    data-testid="purge-days-input"
                  />
                  <span className="text-muted-foreground">days+</span>
                  <Button variant="outline" size="sm" className="h-7 text-[11px] text-red-600 hover:bg-red-50 gap-1" onClick={() => setPurgeConfirm(true)} data-testid="purge-old-btn">
                    <Zap className="h-3 w-3" />Purge Old
                  </Button>
                </>
              )}
            </div>
          )}
          <div className="overflow-y-auto flex-1 -mx-6 px-6">
            {deletedLoading ? (
              <div className="py-12 text-center text-sm text-muted-foreground">Loading deleted transactions...</div>
            ) : deletedTxns.length === 0 ? (
              <div className="py-12 text-center text-sm text-muted-foreground" data-testid="deleted-empty-state">
                <Archive className="h-8 w-8 mx-auto mb-2 opacity-40" />
                <p className="font-medium">No deleted transactions</p>
                <p className="text-xs mt-1">Deleted items will appear here</p>
              </div>
            ) : (
              <div className="rounded-lg border border-border/60 bg-white overflow-hidden">
                <table className="w-full text-xs" data-testid="deleted-table">
                  <thead>
                    <tr className="bg-[#f5f0eb] text-left text-[10px] font-semibold uppercase tracking-wide text-muted-foreground border-b border-border/50">
                      <th className="px-3 py-2 w-8">
                        <input
                          type="checkbox"
                          checked={selectedDeleted.size === deletedTxns.length && deletedTxns.length > 0}
                          onChange={toggleDeletedSelectAll}
                          className="rounded border-gray-300"
                          data-testid="deleted-select-all"
                        />
                      </th>
                      <th className="px-3 py-2">Deleted</th>
                      <th className="px-3 py-2">Date</th>
                      <th className="px-3 py-2">Title / Description</th>
                      <th className="px-3 py-2 text-right">Amount</th>
                      <th className="px-3 py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deletedTxns.map((txn, idx) => (
                      <tr key={txn.id} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-[#fafaf7]'} border-b border-border/30 last:border-b-0`} data-testid="deleted-row">
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={selectedDeleted.has(txn.id)}
                            onChange={() => toggleDeletedSelect(txn.id)}
                            className="rounded border-gray-300"
                            data-testid="deleted-select-checkbox"
                          />
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap tabular-nums text-[10px] text-muted-foreground">
                          {txn.deleted_at ? new Date(txn.deleted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap tabular-nums">{formatDate(txn.date)}</td>
                        <td className="px-3 py-2 max-w-[320px]">
                          {txn.title ? (
                            <span className="font-medium truncate block">{txn.title}</span>
                          ) : (
                            <span className="italic text-muted-foreground truncate block">{txn.description || '—'}</span>
                          )}
                        </td>
                        <td className={`px-3 py-2 whitespace-nowrap font-semibold tabular-nums text-right ${txn.type === 'income' ? 'text-emerald-700' : 'text-red-700'}`}>
                          {txn.type === 'income' ? '+' : '-'}{formatCurrency(txn.amount)}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <div className="inline-flex items-center gap-0.5">
                            <Button variant="ghost" size="sm" className="h-7 text-[11px] text-primary hover:text-primary/80" onClick={() => handleRestore(txn)} data-testid="restore-btn">
                              <RotateCcw className="h-3 w-3 mr-1" />Restore
                            </Button>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-red-600" onClick={() => setPermanentDeleteConfirm(txn)} title="Permanently delete" data-testid="permanent-delete-btn">
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk permanent delete confirmation */}
      <Dialog open={bulkDeleteConfirm} onOpenChange={setBulkDeleteConfirm}>
        <DialogContent className="max-w-sm" data-testid="bulk-delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              Permanently delete {selectedDeleted.size} transaction{selectedDeleted.size === 1 ? '' : 's'}?
            </DialogTitle>
            <DialogDescription>
              This action <span className="font-semibold text-red-600">cannot be undone</span>.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDeleteConfirm(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleBulkPermanentDelete} data-testid="confirm-bulk-delete-btn">Delete Forever</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Purge-old confirmation */}
      <Dialog open={purgeConfirm} onOpenChange={setPurgeConfirm}>
        <DialogContent className="max-w-sm" data-testid="purge-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-amber-600" />
              Purge old deleted transactions?
            </DialogTitle>
            <DialogDescription>
              This will <span className="font-semibold text-red-600">permanently delete</span> all transactions that have been in the trash for more than <strong>{purgeDays} days</strong>. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPurgeConfirm(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handlePurgeOld} disabled={purging} data-testid="confirm-purge-btn">
              {purging ? 'Purging…' : `Purge Older than ${purgeDays}d`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permanent Delete Confirmation */}
      <Dialog open={!!permanentDeleteConfirm} onOpenChange={(v) => { if (!v) setPermanentDeleteConfirm(null); }}>
        <DialogContent className="max-w-sm" data-testid="permanent-delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              Permanently delete?
            </DialogTitle>
            <DialogDescription>
              This action <span className="font-semibold text-red-600">cannot be undone</span>. The transaction will be permanently removed from the database.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPermanentDeleteConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handlePermanentDelete} data-testid="confirm-permanent-delete-btn">
              Permanently Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
